// src/components/admin/DashboardContent.js
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { FaCalendarCheck, FaCommentDots, FaSpinner, FaImage } from 'react-icons/fa';
// Removed: Link import as recent appointment/feedback sections are removed
// import { Link } from 'react-router-dom';
// Removed: format import as recent appointment/feedback sections are removed
// import { format } from 'date-fns';

// Import components to be embedded directly
import VisitorStats from './VisitorStats';
import AdminNotes from './AdminNotes';
// Removed: ImageUpload import as it's requested to be removed from here
// import ImageUpload from './ImageUpload'; // Not used in this version

const API_BASE = process.env.REACT_APP_API_URL;

const DashboardContent = () => {
  const [counts, setCounts] = useState({ appointments: 0, feedback: 0, images: 0 });
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState(''); // Keep general message for errors/auth issues

  // Function to fetch ALL dashboard overview data (runs only once on mount)
  const fetchDashboardOverviewData = useCallback(async () => {
    setLoading(true);
    setMessage('');
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setMessage('Authentication error: Not logged in or token expired.');
        setLoading(false);
        return;
      }

      const headers = { Authorization: `Bearer ${token}` };

      // Fetch necessary data concurrently for the remaining sections
      const [
        newApptCountResponse,
        newFeedbackCountResponse,
        allImagesResponse, // To get total image count
      ] = await Promise.all([
        axios.get(`${API_BASE}/api/appointments/count/new`, { headers, withCredentials: true }),
        axios.get(`${API_BASE}/api/feedback/count/new`, { headers, withCredentials: true }),
        axios.get(`${API_BASE}/api/images`, { headers, withCredentials: true }),
      ]);

      // Process counts for cards
      const newAppointmentsCount = newApptCountResponse.data.count || 0;
      const newFeedbackCount = newFeedbackCountResponse.data.count || 0;
      const totalImagesCount = allImagesResponse.data.length || 0;

      setCounts({
        appointments: newAppointmentsCount,
        feedback: newFeedbackCount,
        images: totalImagesCount,
      });

      // REMOVED: setMessage('Dashboard data loaded.'); // Removed this line as requested
    } catch (error) {
      console.error('Error fetching dashboard overview data:', error.response?.data || error.message);
      setMessage('❌ Failed to load dashboard overview data. Please check backend connections and authentication.');
      // Reset data on error
      setCounts({ appointments: 0, feedback: 0, images: 0 });
    } finally {
      setLoading(false);
    }
  }, []);

  // Effect for initial data load (runs only once on mount)
  useEffect(() => {
    fetchDashboardOverviewData();
    // No setInterval here, ensuring content does not auto-refresh
  }, [fetchDashboardOverviewData]);

  // Effect to clear temporary messages (only for error messages now)
  useEffect(() => {
    if (message && !message.startsWith('✅')) { // Only clear if it's not a success message (which we removed anyway)
      const timer = setTimeout(() => setMessage(''), 5000); // Clear message after 5 seconds
      return () => clearTimeout(timer);
    }
  }, [message]);

  return (
    <div className="p-6 bg-gray-100 min-h-screen">
      <h2 className="text-3xl font-bold text-gray-800 mb-6">Admin Dashboard Overview</h2>

      {message && (
        <div className={`p-3 mb-4 rounded-md ${message.startsWith('✅') ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
          {message}
        </div>
      )}

      {loading ? (
        <div className="flex justify-center items-center h-64">
          <FaSpinner className="animate-spin text-cyan-600 text-4xl" />
          <p className="ml-3 text-gray-600">Loading dashboard data...</p>
        </div>
      ) : (
        <>
          {/* Stats Cards: New Appointments, New Feedback, Total Images, Visitor Stats */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <div className="bg-white p-6 rounded-lg shadow-md flex items-center justify-between">
              <div>
                <h3 className="text-xl font-semibold text-gray-700">New Appointments</h3>
                <p className="text-3xl font-bold text-cyan-600">{counts.appointments}</p>
              </div>
              <FaCalendarCheck className="text-cyan-400 text-4xl" />
            </div>

            <div className="bg-white p-6 rounded-lg shadow-md flex items-center justify-between">
              <div>
                <h3 className="text-xl font-semibold text-gray-700">New Feedback</h3>
                <p className="text-3xl font-bold text-indigo-600">{counts.feedback}</p>
              </div>
              <FaCommentDots className="text-indigo-400 text-4xl" />
            </div>

            {/* Total Images Card */}
            <div className="bg-white p-6 rounded-lg shadow-md flex items-center justify-between">
              <div>
                <h3 className="text-xl font-semibold text-gray-700">Total Images</h3>
                <p className="text-3xl font-bold text-green-600">{counts.images}</p>
              </div>
              <FaImage className="text-green-400 text-4xl" />
            </div>

            {/* Visitor Stats Card (from VisitorStats component) */}
            <VisitorStats />
          </div>

          {/* Embedded Admin Notes Section */}
          <div className="mb-6">
            <AdminNotes />
          </div>

          {/* Removed: Recent Appointments Section */}
          {/* Removed: Recent Feedback Section */}
          {/* Removed: Image Upload Section */}
        </>
      )}
    </div>
  );
};

export default DashboardContent;